/**
 * ConfigWidgetEditor — Config avanzata per campi tabella_mensile / array_ripetibile.
 *
 * Presenta una lista editabile di:
 *   - `columns` (per tabella_mensile)
 *   - `sotto_campi` (per array_ripetibile), con `min_rows` / `max_rows`
 *
 * Salvataggio bulk via PATCH /admin/report-schema/sezioni/{sez}/campi/{campo}/config.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, GripVertical, Save } from "lucide-react";
import SortableList from "@/components/SortableList";

const COL_TIPI_TABELLA = [
  { value: "number", label: "Numero" },
  { value: "text", label: "Testo" },
  { value: "enum", label: "Enum" },
  { value: "bool", label: "Sì/No" },
  { value: "date", label: "Data" },
  { value: "readonly_label", label: "Etichetta (readonly)" },
];

const COL_TIPI_ARRAY = [
  { value: "text", label: "Testo" },
  { value: "textarea", label: "Testo lungo" },
  { value: "enum", label: "Enum" },
  { value: "number", label: "Numero" },
  { value: "bool", label: "Sì/No" },
  { value: "date", label: "Data" },
];

const AGGREGATE_OPTS = [
  { value: "__none__", label: "— nessuno —" },
  { value: "sum", label: "Somma YTD" },
  { value: "avg", label: "Media YTD" },
];

const TARGET_KEYS_COL = [
  { value: "__none__", label: "— nessuno —" },
  { value: "booking_target", label: "Booking target" },
  { value: "pulizia_booking_target", label: "Pulizia Booking target" },
  { value: "google_target", label: "Google target" },
  { value: "transfer_annuale", label: "Transfer annuale" },
  { value: "food_annuale", label: "Food annuale" },
  { value: "fatturato_annuale", label: "Fatturato annuale" },
];

export default function ConfigWidgetEditor({ open, onOpenChange, sezKey, campo, onSaved }) {
  const isTabella = campo?.tipo === "tabella_mensile";
  const itemsKey = isTabella ? "columns" : "sotto_campi";
  const [items, setItems] = useState([]);
  const [minRows, setMinRows] = useState(0);
  const [maxRows, setMaxRows] = useState("");
  const [busy, setBusy] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);

  useEffect(() => {
    if (!open || !campo) return;
    const cfg = campo.config || {};
    const list = (cfg[itemsKey] || []).slice().sort((a, b) => (a.ordine || 0) - (b.ordine || 0))
      .map((i, idx) => ({ ...i, _dnd_id: i.key || `__new_${idx}_${Math.random().toString(36).slice(2, 8)}` }));
    setItems(JSON.parse(JSON.stringify(list)));
    setMinRows(cfg.min_rows || 0);
    setMaxRows(cfg.max_rows == null ? "" : cfg.max_rows);
  }, [open, campo, itemsKey]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((it, i) => (it.ordine = i + 1));
    setItems(next);
  };

  const onReorder = (newList) => {
    setItems(newList.map((it, i) => ({ ...it, ordine: i + 1 })));
  };

  const addNew = () => {
    setEditItem({
      label: "", tipo: isTabella ? "number" : "text",
      key: "", required: false,
      ...(isTabella ? { aggregate: "" } : {}),
    });
    setEditIndex(-1);
  };

  const openEdit = (it, idx) => { setEditItem(JSON.parse(JSON.stringify(it))); setEditIndex(idx); };

  const removeItem = (idx) => {
    const it = items[idx];
    if (it.system) return toast.error("Elemento di sistema: non rimovibile");
    setItems(items.filter((_, i) => i !== idx).map((x, i) => ({ ...x, ordine: i + 1 })));
  };

  const commitEditItem = () => {
    if (!editItem?.label?.trim()) return toast.error("Label obbligatorio");
    const clean = { ...editItem, ordine: editIndex === -1 ? items.length + 1 : items[editIndex].ordine };
    if (clean.tipo === "enum" && (!clean.enum_options || clean.enum_options.length === 0)) {
      return toast.error("Enum richiede almeno una opzione");
    }
    // Parse min/max/step come numeri
    ["min", "max", "step", "decimals"].forEach((k) => {
      if (clean[k] === "" || clean[k] == null) delete clean[k];
      else clean[k] = parseFloat(clean[k]);
    });
    let next;
    if (editIndex === -1) {
      clean._dnd_id = `__new_${items.length}_${Math.random().toString(36).slice(2, 8)}`;
      next = [...items, clean];
    } else {
      clean._dnd_id = items[editIndex]._dnd_id || clean.key || `__idx_${editIndex}`;
      next = items.map((x, i) => (i === editIndex ? clean : x));
    }
    setItems(next);
    setEditItem(null); setEditIndex(-1);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = isTabella
        ? { columns: items }
        : { sotto_campi: items, min_rows: parseInt(minRows) || 0, max_rows: maxRows === "" ? null : parseInt(maxRows) };
      const { data } = await api.patch(`/admin/report-schema/sezioni/${sezKey}/campi/${campo.key}/config`, payload);
      toast.success(`Config aggiornata (schema v${data.version})`);
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!campo) return null;
  const tipiOptions = isTabella ? COL_TIPI_TABELLA : COL_TIPI_ARRAY;
  const title = isTabella ? "Config colonne tabella mensile" : "Config sotto-campi array ripetibile";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="config-widget-editor">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{title}</span>
              <code className="text-xs px-1.5 py-0.5 rounded bg-accent/40">{campo.label}</code>
            </DialogTitle>
            <DialogDescription>
              {isTabella
                ? "Aggiungi/rimuovi colonne della tabella. Le colonne di sistema (mese, stato) non sono rimovibili."
                : "Aggiungi/rimuovi sotto-campi che compongono ogni riga dell'array. Definisci min/max righe se necessario."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-2">
            <SortableList items={items} getId={(it) => it._dnd_id || it.key} onReorder={onReorder}>
              {(it, sp) => {
                const idx = items.indexOf(it);
                return (
                  <div
                    ref={sp.setNodeRef}
                    style={sp.style}
                    className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 bg-background/40"
                    data-testid={`cfg-item-${idx}`}
                  >
                    <button
                      {...sp.attributes}
                      {...sp.listeners}
                      className="p-1 cursor-grab active:cursor-grabbing touch-none text-muted-foreground opacity-70 hover:opacity-100"
                      aria-label={`Trascina ${it.label}`}
                      data-testid={`cfg-handle-${idx}`}
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{it.label}</span>
                        {it.key && <code className="text-[10px] px-1 py-0.5 rounded bg-accent/40">{it.key}</code>}
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-lavender-100 text-lavender-700 dark:bg-lavender-950/40">{it.tipo}</span>
                        {it.system && <span className="text-[9px] uppercase text-slate-500 font-medium">system</span>}
                        {it.required && <span className="text-[9px] uppercase text-rose-500 font-medium">req</span>}
                        {it.aggregate && <span className="text-[9px] text-emerald-600">agg: {it.aggregate}</span>}
                        {it.target_key_hotel && <span className="text-[9px] text-emerald-600">target: {it.target_key_hotel}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it, idx)} data-testid={`cfg-item-edit-${idx}`}>Modifica</Button>
                    <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={it.system} className="text-rose-500" data-testid={`cfg-item-remove-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              }}
            </SortableList>
            {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Nessun elemento definito.</p>}
          </div>

          {!isTabella && (
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/60">
              <div>
                <Label className="text-xs">Min righe</Label>
                <Input type="number" min="0" value={minRows} onChange={(e) => setMinRows(e.target.value)} data-testid="cfg-min-rows" />
              </div>
              <div>
                <Label className="text-xs">Max righe (vuoto = illimitato)</Label>
                <Input type="number" min="0" value={maxRows} onChange={(e) => setMaxRows(e.target.value)} data-testid="cfg-max-rows" />
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 border-t border-border/60 flex-shrink-0">
            <Button variant="outline" onClick={addNew} data-testid="cfg-add-item">
              <Plus className="h-4 w-4" /> Aggiungi {isTabella ? "colonna" : "sotto-campo"}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button onClick={save} disabled={busy} data-testid="cfg-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-modal: edit/create item */}
      <Dialog open={editItem !== null} onOpenChange={(o) => { if (!o) { setEditItem(null); setEditIndex(-1); } }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="cfg-item-editor">
          <DialogHeader>
            <DialogTitle>{editIndex === -1 ? "Nuovo elemento" : "Modifica elemento"}</DialogTitle>
            {editItem?.system && <DialogDescription className="text-amber-600">Elemento di sistema: label editabile, key e tipo bloccati.</DialogDescription>}
          </DialogHeader>
          {editItem && (
            <div className="space-y-2">
              <Label>Label</Label>
              <Input value={editItem.label || ""} onChange={(e) => setEditItem({ ...editItem, label: e.target.value })} data-testid="cfg-item-label" autoFocus />
              {editIndex === -1 && (
                <>
                  <Label>Key tecnica (opzionale)</Label>
                  <Input value={editItem.key || ""} onChange={(e) => setEditItem({ ...editItem, key: e.target.value })}
                    placeholder="Auto-generata dalla label" data-testid="cfg-item-key" />
                </>
              )}
              <Label>Tipo</Label>
              <Select value={editItem.tipo} onValueChange={(v) => setEditItem({ ...editItem, tipo: v })} disabled={editItem.system}>
                <SelectTrigger data-testid="cfg-item-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{tipiOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>

              {editItem.tipo === "number" && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Min</Label><Input type="number" value={editItem.min ?? ""} onChange={(e) => setEditItem({ ...editItem, min: e.target.value })} /></div>
                    <div><Label className="text-xs">Max</Label><Input type="number" value={editItem.max ?? ""} onChange={(e) => setEditItem({ ...editItem, max: e.target.value })} /></div>
                    <div><Label className="text-xs">Step</Label><Input type="number" value={editItem.step ?? ""} onChange={(e) => setEditItem({ ...editItem, step: e.target.value })} /></div>
                  </div>
                  {isTabella && (
                    <>
                      <Label>Aggregazione YTD</Label>
                      <Select value={editItem.aggregate || "__none__"} onValueChange={(v) => setEditItem({ ...editItem, aggregate: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Nessuna" /></SelectTrigger>
                        <SelectContent>{AGGREGATE_OPTS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Label>Target hotel (confronto)</Label>
                      <Select value={editItem.target_key_hotel || "__none__"} onValueChange={(v) => setEditItem({ ...editItem, target_key_hotel: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Nessuno" /></SelectTrigger>
                        <SelectContent>{TARGET_KEYS_COL.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  )}
                </>
              )}

              {editItem.tipo === "enum" && (
                <>
                  <Label>Opzioni enum (una per riga, formato value|Label)</Label>
                  <Textarea
                    value={(editItem.enum_options || []).map((o) => `${o.value}|${o.label}`).join("\n")}
                    onChange={(e) => setEditItem({
                      ...editItem,
                      enum_options: e.target.value.split("\n").filter(Boolean).map((r) => {
                        const [v, l] = r.split("|");
                        return { value: (v || "").trim(), label: (l || v || "").trim() };
                      })
                    })}
                    className="min-h-[100px] font-mono text-xs"
                    disabled={editItem.system}
                  />
                </>
              )}

              {!isTabella && (
                <div className="flex items-center gap-2 pt-2">
                  <Switch checked={!!editItem.required} onCheckedChange={(v) => setEditItem({ ...editItem, required: v })} />
                  <Label className="text-xs">Obbligatorio (soft warning UI)</Label>
                </div>
              )}
              {!isTabella && (
                <div className="flex items-center gap-2">
                  <Switch checked={editItem.col_span === 2} onCheckedChange={(v) => setEditItem({ ...editItem, col_span: v ? 2 : 1 })} />
                  <Label className="text-xs">Larghezza doppia (span 2 colonne)</Label>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditItem(null); setEditIndex(-1); }}>Annulla</Button>
            <Button onClick={commitEditItem} data-testid="cfg-item-editor-save">
              <Save className="h-4 w-4" /> {editIndex === -1 ? "Aggiungi" : "Applica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
